import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import jsQR from "jsqr";

type ScanState = "idle" | "requesting" | "scanning" | "error" | "insecure";

export default function ReapplyWaitingPage() {
  const navigate = useNavigate();
  const [scanState, setScanState] = useState<ScanState>("idle");
  const [camError, setCamError] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number>(0);

  // Guard: no prefill → back to history
  useEffect(() => {
    const raw = localStorage.getItem("fudge_prefill");
    if (!raw) navigate("/fudges/history", { replace: true });
  }, [navigate]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => stopCamera();
  }, []);

  function stopCamera() {
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  async function startCamera() {
    // Camera requires a secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      setScanState("insecure");
      return;
    }
    setScanState("requesting");
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current!;
      video.srcObject = stream;
      await video.play();
      setScanState("scanning");
      scan();
    } catch {
      setCamError("Camera access denied or unavailable.");
      setScanState("error");
    }
  }

  function scan() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(scan);
      return;
    }
    const ctx = canvas.getContext("2d")!;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
    if (code) {
      // Match http(s)://<host>/scan/<tagId>
      const match = code.data.match(/\/scan\/([^/?#]+)/);
      if (match) {
        stopCamera();
        navigate(`/scan/${match[1]}`);
        return;
      }
    }
    rafRef.current = requestAnimationFrame(scan);
  }

  function handleCancel() {
    stopCamera();
    localStorage.removeItem("fudge_prefill");
    navigate("/dashboard");
  }

  const prefill = getPrefill();

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center pt-8 px-4 pb-8">
      <div className="w-full max-w-sm space-y-4">

        {/* Header */}
        <div className="bg-white rounded-xl p-6 shadow-sm text-center">
          <div className="text-5xl mb-3">📷</div>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Ready to re-apply</h1>
          <p className="text-sm text-slate-500">
            Scan an unused QR tag and the details below will be pre-filled for you.
          </p>
        </div>

        {/* Pre-fill summary */}
        {prefill && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-0.5">
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Pre-fill loaded</p>
            <p className="text-sm font-medium text-blue-900">{prefill.description}</p>
            {prefill.changeDetail && <p className="text-xs text-blue-700">{prefill.changeDetail}</p>}
            {prefill.location && <p className="text-xs text-blue-500">📍 {prefill.location}</p>}
          </div>
        )}

        {/* Camera area */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {scanState === "idle" && (
            <button
              onClick={startCamera}
              className="w-full py-12 flex flex-col items-center gap-3 text-slate-500 hover:bg-slate-50 transition-colors"
            >
              <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z" />
              </svg>
              <span className="text-sm font-medium">Tap to open camera</span>
            </button>
          )}

          {scanState === "requesting" && (
            <div className="py-12 text-center text-slate-400 text-sm">Requesting camera…</div>
          )}

          {(scanState === "error" || scanState === "insecure") && (
            <div className="py-6 px-6 space-y-4">
              {scanState === "insecure" ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  In-app camera requires HTTPS. Use your phone's camera app instead — the pre-fill will still apply automatically when you scan.
                </p>
              ) : (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  {camError} — use your phone's camera app to scan the QR tag instead.
                </p>
              )}
              <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                <li>Open your phone's camera app</li>
                <li>Point it at an unused QR tag</li>
                <li>Tap the link that appears — details will be pre-filled</li>
              </ol>
              {scanState === "error" && (
                <button onClick={startCamera} className="text-sm text-blue-600 underline">Try camera again</button>
              )}
            </div>
          )}

          {/* Video viewfinder — always rendered so ref is available */}
          <div className={scanState === "scanning" ? "block" : "hidden"}>
            <div className="relative">
              <video
                ref={videoRef}
                muted
                playsInline
                className="w-full rounded-xl"
              />
              {/* Corner overlay to indicate scan area */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 relative">
                  <span className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-md" />
                  <span className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-md" />
                  <span className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-md" />
                  <span className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-md" />
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-slate-400 py-3">Point at a QR tag…</p>
          </div>
        </div>

        {/* Hidden canvas for frame processing */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Cancel */}
        <button
          onClick={handleCancel}
          className="w-full py-3 text-sm text-slate-500 border border-slate-200 rounded-xl bg-white hover:bg-slate-50"
        >
          Cancel — forget pre-fill and go to dashboard
        </button>
      </div>
    </div>
  );
}

function getPrefill() {
  const raw = localStorage.getItem("fudge_prefill");
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
